import {
  ENTITY_CHECKS,
  isParamToken,
  isVariable,
  OPERATORS,
  parseWizardEntityCheck,
  parseWizardRule,
  type Action,
  type Condition,
  type EntityCheck,
  type ParameterKind,
  type RiskTier,
  type Rule,
  type WizardEntityCheck,
  type WizardParameter,
  type WizardRule,
} from "./clearanceRulesConfig";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isCondition(value: unknown): value is Condition {
  if (!isRecord(value)) return false;
  if (Array.isArray(value.all)) return value.all.length > 0 && value.all.every(isCondition);
  if (Array.isArray(value.any)) return value.any.length > 0 && value.any.every(isCondition);
  return (
    typeof value.name === "string" &&
    isVariable(value.name) &&
    typeof value.operator === "string" &&
    (OPERATORS as readonly string[]).includes(value.operator) &&
    (typeof value.value === "number" || isParamToken(value.value))
  );
}

function isAction(value: unknown): value is Action {
  if (!isRecord(value) || value.name !== "set_infringement_class") return false;
  if (!isRecord(value.params)) return false;
  return (
    typeof value.params.key === "string" &&
    (typeof value.params.value === "number" || isParamToken(value.params.value))
  );
}

// Structural validation for one stored rule - guards parseStoredConfig
// against a config that's missing fields, shaped unexpectedly, or otherwise
// not what this wizard produced (e.g. hand-edited, or written by a future/
// past version of this schema). An invalid rule is skipped rather than
// thrown, so one bad entry can't block loading the rest of a system's config.
function isRule(value: unknown): value is Rule {
  if (!isRecord(value)) return false;
  return (
    isCondition(value.conditions) &&
    Array.isArray(value.actions) &&
    value.actions.length > 0 &&
    value.actions.every(isAction)
  );
}

function isRiskTier(value: unknown): value is RiskTier {
  if (!isRecord(value)) return false;
  return (
    typeof value.level === "number" &&
    (value.part_will_hit_level === undefined || typeof value.part_will_hit_level === "number")
  );
}

function isEntityCheck(value: unknown): value is EntityCheck {
  if (!isRecord(value)) return false;
  if (typeof value.axis !== "string") return false;
  if (value.isolated_axis !== undefined && typeof value.isolated_axis !== "string") return false;
  if (typeof value.check !== "string" || !(ENTITY_CHECKS as readonly string[]).includes(value.check)) {
    return false;
  }
  if (!isRecord(value.params)) return false;
  if (value.params.risk_tree !== undefined && !isRiskTier(value.params.risk_tree)) return false;
  if (value.params.lone_risk_tree !== undefined && !isRiskTier(value.params.lone_risk_tree)) return false;
  return true;
}

function parseParameters(value: unknown): WizardParameter[] {
  if (!isRecord(value)) return [];
  const { metric, non_metric } = value;

  const collect = (kind: ParameterKind, map: unknown): WizardParameter[] => {
    if (!isRecord(map)) return [];
    return Object.entries(map)
      .filter((entry): entry is [string, number] => typeof entry[1] === "number")
      .map(([rawName, paramValue]) => ({
        key: crypto.randomUUID(),
        name: rawName.startsWith("$") ? rawName.slice(1) : rawName,
        kind,
        value: paramValue,
      }));
  };

  return [...collect("metric", metric), ...collect("non_metric", non_metric)];
}

// A system's stored config, as loosely typed on LidarClearanceSystem - parsed
// back into wizard state both when selecting a system (loading its own saved
// config) and when copying another system's config into the current draft.
// Defensive throughout: `config` (or any field on it, e.g. a missing/renamed
// `axes`, `rules` not being an array, one malformed rule) reflects whatever
// is actually stored, not necessarily what this wizard last wrote - anything
// that doesn't validate is dropped rather than thrown, and counted in
// `skippedCount` so the caller can warn instead of silently losing data.
export function parseStoredConfig(config: Record<string, unknown> | null | undefined): {
  rules: WizardRule[];
  entityChecks: WizardEntityCheck[];
  parameters: WizardParameter[];
  skippedCount: number;
} {
  const rawRules = Array.isArray(config?.rules) ? config.rules : [];
  const rawEntityChecks = Array.isArray(config?.entity_checks) ? config.entity_checks : [];

  const validRules = rawRules.filter(isRule);
  const validEntityChecks = rawEntityChecks.filter(isEntityCheck);

  return {
    rules: validRules.map(parseWizardRule),
    entityChecks: validEntityChecks.map(parseWizardEntityCheck),
    parameters: parseParameters(config?.parameters),
    skippedCount:
      rawRules.length - validRules.length + (rawEntityChecks.length - validEntityChecks.length),
  };
}
